const {
    config: { defaultValueSelectorNames }
} = require('@sstdev/lib_metadata-config');
const colors = require('./colorTransforms');
const logging = require('@sstdev/lib_logging');
const cloneDeep = require('lodash/cloneDeep');
const buildTree = require('../blockGroupConfigurations');
const addWorkspaceMenuOptions = require('./contextMenus');
const configureDataAccess = require('./configureDataAccess');
const cutAndPaste = require('../../utilities/cutAndPaste');
const Blockly = require('blockly');
const { setReverseOrder } = require('./overrideJsGenerateForJson');
require('./overrideJsGenerateForJson');
require('./overrideBlocklyStyleInjection');
require('./overrideBlocklyWorkspaceLoad');

const EMPTY_TOOLBOX = {
    kind: 'categoryToolbox',
    contents: []
};

// Indirection to allow test spies/stubs to work;
const toExport = {
    getBuilder,
    getBuildTree,
    configureWorkspace
};

module.exports = toExport;

function getBuilder(groupName, categorySpec, color, subSet) {
    let GroupBuilder;
    if (categorySpec.addSeparator) {
        GroupBuilder = require('../blockGroups/_separatorBuilder');
        return new GroupBuilder();
    }
    GroupBuilder = require('../blockGroups/' + groupName + 's');
    return new GroupBuilder(groupName, categorySpec, color, subSet);
}

function getBuildTree() {
    return buildTree;
}

/**
 * @typedef {Object} Toolbox
 * @property {string} kind - The kind of toolbox object.
 * @property {ToolboxCategory[]} contents - The contents of the toolbox object.
 * @typedef {Object} ToolboxCategory
 * @property {string} kind - The kind of toolbox object.
 * @property {string} name - The name of the toolbox object.
 * @property {string} colour - The colour of the toolbox object.
 * @property {Array} contents - The contents of the toolbox object.
 * @typedef {ToolboxBlock[]} ToolboxBlock
 * @property {string} kind - The kind of toolbox object.
 * @property {string} type - The type of the toolbox object.
 */

/**
 * Configures the Blockly workspace.
 *
 * @param {string} [subSet=''] - The subset to configure Blockly for.
 * @param {Array} namespaces - The namespaces required to be passed in.
 * @param {Array} [defaultValueSelectors=defaultValueSelectorNames] - The default value selectors.
 * @returns {Toolbox} - The configured toolbox.
 * @throws {Error} - If namespaces are not provided or not an array.
 */
function configureWorkspace(subSet = '', namespaces, defaultValueSelectors = defaultValueSelectorNames, reverseOrder = false) {
    if (typeof subSet !== 'string') {
        subSet = '';
    }
    if (!global.__UNIT_TESTING__) {
        logging.info(`[BLOCKLY] Configuring Blockly ${subSet && `for ${subSet}`}`);
    }

    if (!namespaces) {
        throw new Error('Namespaces are required to be passed in');
    }
    if (!Array.isArray(namespaces)) {
        throw new Error('Namespaces should be an array');
    }

    // I can't figure out why using lib_block-chain reverses the children and property
    // order, so this is a workaround for now.
    setReverseOrder(reverseOrder);

    if (Blockly.ContextMenuRegistry.registry.getItem('blockCopyToStorage') == null) {
        const options = {
            shortcut: true
        };
        const plugin = new cutAndPaste();
        plugin.init(options, e => {
            logging.error(e);
        });
    }

    configureDataAccess(namespaces, defaultValueSelectors);

    const toolbox = cloneDeep(EMPTY_TOOLBOX);

    const allSections = Object.entries(toExport.getBuildTree());
    //assign consistent coloring
    const toolboxColors = colors.getColors(allSections.filter(([, spec]) => !spec.color && !spec.addSeparator).length);
    let colorIndex = 0;
    allSections.forEach(([, section]) => {
        if (!section.color && !section.addSeparator) {
            section.color = toolboxColors[colorIndex];
            colorIndex++;
        }
    });

    //now filter down to those that apply to this subset
    const sections = allSections.filter(([, value]) => !subSet || !value.subSet || value.subSet.includes(subSet));

    sections.forEach(([nodeTypeGroup, categorySpec]) => {
        let groupBuilder = toExport.getBuilder(nodeTypeGroup, categorySpec, categorySpec.color, subSet);
        if (groupBuilder) {
            //build up the toolbox, with a category based on references
            groupBuilder.addToolboxCategory(toolbox);
            //build up the block definition for each of the references added to the toolbox
            groupBuilder.addToBlocks();
            //define how a block as defined above translates to (JSON) output:
            groupBuilder.addCodeGenerator();
        } else {
            logging.error(`Build methods are not defined for the ${nodeTypeGroup} block section.`);  
        }
    });
    addWorkspaceMenuOptions();

    return toolbox;
}
